import React from 'react';
import SPAPill from '../../shared/SPAPill';
import type { SPAPillTone } from '../../shared/SPAPill';
import type { AssignmentRow, AssignmentStatus } from '../useAssignmentRows';

// AIDEV-NOTE: `covered_by_twin` is neutral on purpose. Those assignments deliver nothing and are
// still not a problem — a duplicate on the same content already put every affected student's grade
// in the LMS. Showing them in the same red as a real failure was 71 of one teacher's 204 rows.
const STATUS: Record<AssignmentStatus, { label: string; tone: SPAPillTone }> = {
  covered_by_twin: { label: 'Covered by a duplicate', tone: 'neutral' },
  delivering: { label: 'Delivering', tone: 'success' },
  not_delivering: { label: 'Not delivering', tone: 'error' },
  not_in_lms: { label: 'Made in TCI', tone: 'neutral' },
  nothing_to_sync: { label: 'Nothing to sync', tone: 'neutral' },
  partial: { label: 'Partly delivering', tone: 'warning' },
  rejected: { label: 'Rejected by the LMS', tone: 'error' },
};

// The worst finding, tagged with who acts on it (TCI, LMS, or both), all of them on hover.
const Diagnosis = ({ found, owner }: { found: string[]; owner?: string }) => (
  <div className="tw-mt-1 tw-text-sm tw-text-spa-secondary" title={found.join(' · ')}>
    {owner != null && (
      <span className="tw-mr-1.5 tw-font-mono tw-text-xs tw-font-semibold tw-text-spa-tertiary">
        {owner.toUpperCase()}
      </span>
    )}
    {found[0]}
    {found.length > 1 && <span className="tw-text-spa-tertiary">{` +${found.length - 1}`}</span>}
  </div>
);

interface StatusCellProps {
  row: AssignmentRow;
  showDiagnosis?: boolean;
}

// The counts that used to be three columns of mostly zeros, said only when they are not zero. Out of
// date is the worst of them: the gradebook looks right and isn't. Rate limited and skipped never
// reached the LMS, so they are not failures.
const StatusCell = ({ row, showDiagnosis = false }: StatusCellProps) => {
  const { refused_locally: limited = 0, rejected_now: rejected, skipped = 0, stale } = row;
  const problems = [stale > 0 && `${stale} out of date`, rejected > 0 && `${rejected} rejected now`].filter(Boolean);
  const held = [limited > 0 && `${limited} rate limited`, skipped > 0 && `${skipped} skipped`].filter(Boolean);

  return (
    <div>
      <SPAPill tone={STATUS[row.status].tone}>{STATUS[row.status].label}</SPAPill>
      {problems.length > 0 && <div className="tw-mt-1 tw-text-sm tw-text-spa-error">{problems.join(' · ')}</div>}
      {held.length > 0 && <div className="tw-mt-1 tw-text-sm tw-text-spa-tertiary">{held.join(' · ')}</div>}
      {showDiagnosis && row.diagnosis.length > 0 && <Diagnosis found={row.diagnosis} owner={row.owner} />}
    </div>
  );
};

export default StatusCell;
